import { getConfig, postCheck } from '../../core/rest';
import { clear, el } from '../../core/dom';
import { makeBanner, makeChip, makeFieldHead, makePanel, setChip } from '../../core/shell';

type Signal = { id: string; state: string; detail: string; severity: string };
type WpResponse = {
	status: string;
	checked_at: string;
	target: { display_host: string; final_url: string; http_status: number };
	wordpress: string;
	wordpress_label: string;
	signals: Signal[];
	security_headers: Array<{ header: string; state: string; evidence: string }>;
	limitations: string[];
};

function idlePanels(panels: HTMLElement): void {
	clear(panels);
	panels.appendChild(
		makePanel(
			'Public signals',
			el('p', { className: 'kb-tools__muted' }, [
				'Confirm authorization, then check a domain to review publicly visible WordPress signals and shared security-header observations.',
			])
		)
	);
}

function renderResult(panels: HTMLElement, data: WpResponse): void {
	clear(panels);

	const meta = el('dl', { className: 'kb-tools__claim-list' });
	const rows: Array<[string, string]> = [
		['WordPress', data.wordpress_label],
		['Host', data.target.display_host],
		['Final URL', data.target.final_url],
		['HTTP status', String(data.target.http_status)],
		['Checked at', data.checked_at],
	];
	for (const [k, v] of rows) {
		meta.appendChild(el('dt', {}, [k]));
		meta.appendChild(el('dd', {}, [v]));
	}
	panels.appendChild(makePanel('Target', meta));

	const sigList = el('ul', { className: 'kb-tools__bullets' });
	if (!data.signals.length) {
		sigList.appendChild(el('li', { className: 'kb-tools__muted' }, ['No public signals recorded.']));
	} else {
		for (const s of data.signals) {
			const tone =
				s.state === 'observed' || s.severity === 'info'
					? 'ok'
					: s.severity === 'high'
						? 'error'
						: 'warn';
			const item = el('li', { className: 'kb-tools__signal' });
			item.appendChild(
				el('span', { className: `kb-tools__chip kb-tools__chip--${tone}` }, [s.state])
			);
			item.appendChild(el('span', {}, [s.detail]));
			sigList.appendChild(item);
		}
	}
	panels.appendChild(makePanel('Public signals', sigList));

	const hdrList = el('ul', { className: 'kb-tools__bullets' });
	for (const h of data.security_headers || []) {
		const label = h.state === 'observed' ? 'Observed' : 'Not observed';
		const tone = h.state === 'observed' ? 'ok' : 'warn';
		const item = el('li', { className: 'kb-tools__signal' });
		item.appendChild(el('span', { className: 'kb-tools__signal-name' }, [h.header]));
		item.appendChild(el('span', { className: `kb-tools__chip kb-tools__chip--${tone}` }, [label]));
		if (h.evidence) {
			item.appendChild(el('span', { className: 'kb-tools__muted' }, [h.evidence]));
		}
		hdrList.appendChild(item);
	}
	panels.appendChild(makePanel('Security headers (shared check)', hdrList));
}

export function mountWordPressChecker(root: HTMLElement): void {
	root.classList.add('kb-tools', 'kb-tools--wp');
	root.querySelectorAll('.kb-tools__ssr-fallback').forEach((n) => n.remove());

	const input = el('input', {
		className: 'kb-tools__input',
		id: 'kb-wp-url',
		type: 'text',
		autocomplete: 'off',
		spellcheck: 'false',
		placeholder: 'https://example.org',
		'aria-describedby': 'kb-wp-status kb-wp-privacy',
	}) as HTMLInputElement;

	const auth = el('input', { type: 'checkbox', id: 'kb-wp-auth' }) as HTMLInputElement;
	const authLabel = el('label', { className: 'kb-tools__check', for: 'kb-wp-auth' });
	authLabel.append(
		auth,
		document.createTextNode(
			' I own this site or am authorized to assess its public configuration (educational acknowledgement only).'
		)
	);

	const btn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--primary' }, [
		'Check publicly visible configuration',
	]) as HTMLButtonElement;
	const clearBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Clear',
	]) as HTMLButtonElement;

	const compose = el('div', { className: 'kb-tools__compose' }, [
		makeFieldHead(
			el('label', { className: 'kb-tools__label', for: 'kb-wp-url' }, ['Domain or URL']),
			'Enter to check'
		),
		input,
		authLabel,
		el('div', { className: 'kb-tools__actions' }, [btn, clearBtn]),
	]);

	const chip = makeChip('kb-wp-status');
	const warnings = el('ul', { className: 'kb-tools__warnings' });
	const panels = el('div', { className: 'kb-tools__panels kb-tools__panels--stack' });
	const results = el('div', { className: 'kb-tools__results' }, [
		el('div', { className: 'kb-tools__status-row' }, [chip]),
		warnings,
		panels,
	]);

	clear(root);
	root.append(
		makeBanner(
			'Remote',
			'A restricted public request will be sent. This is a passive review — not a vulnerability assessment or malware scan.'
		),
		el('div', { className: 'kb-tools__workspace kb-tools__workspace--remote' }, [compose, results]),
		el('p', { className: 'kb-tools__privacy', id: 'kb-wp-privacy' }, [
			'No credential testing, directory enumeration, or exploit checks are performed.',
		])
	);

	const cfg = getConfig();
	if (!cfg.remoteEnabled) {
		setChip(chip, 'Remote checks disabled', 'warn');
	} else {
		setChip(chip, 'Waiting for a URL', 'idle');
	}
	idlePanels(panels);

	const run = async (): Promise<void> => {
		if (!auth.checked) {
			setChip(chip, 'Confirm authorization first', 'warn');
			return;
		}
		clear(warnings);
		setChip(chip, 'Checking…', 'idle');
		btn.disabled = true;
		try {
			const data = await postCheck<WpResponse>('check/wordpress', { url: input.value.trim() });
			setChip(chip, `${data.wordpress_label} · ${data.target.display_host}`, 'ok');
			for (const lim of data.limitations || []) {
				warnings.appendChild(el('li', {}, [lim]));
			}
			renderResult(panels, data);
		} catch (err) {
			setChip(chip, err instanceof Error ? err.message : 'Could not check', 'error');
			idlePanels(panels);
		} finally {
			btn.disabled = false;
		}
	};

	btn.addEventListener('click', () => {
		void run();
	});
	clearBtn.addEventListener('click', () => {
		input.value = '';
		auth.checked = false;
		clear(warnings);
		setChip(chip, cfg.remoteEnabled ? 'Waiting for a URL' : 'Remote checks disabled', cfg.remoteEnabled ? 'idle' : 'warn');
		idlePanels(panels);
		input.focus();
	});
	input.addEventListener('keydown', (event) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			void run();
		}
	});
}
